import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigationType } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Menu, PanelLeftOpen, Search, X } from "lucide-react";
import { Mark } from "./Mark";
import { Sidebar } from "./Sidebar";
import { CommandPalette } from "./CommandPalette";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, isMac } from "@/lib/utils";

const SIDEBAR_KEY = "minne.sidebar";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "collapsed";
  } catch {
    return false;
  }
}

export function Layout() {
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  // Desktop sidebar visibility; remembered per browser. Ctrl/⌘+B toggles it.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType !== "POP" && !window.location.hash) window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? "collapsed" : "open");
    } catch {
      // private mode or blocked storage: the choice just does not persist
    }
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((o) => !o);
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-app">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 hidden w-[calc(16rem+env(safe-area-inset-left))] border-r border-sidebar-border bg-sidebar pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] text-sidebar-foreground",
          !collapsed && "md:block"
        )}
      >
        <Sidebar onOpenPalette={() => setPalette(true)} onCollapse={() => setCollapsed(true)} />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-[env(safe-area-inset-top)] z-30 flex h-13 items-center gap-2 border-b bg-background/90 px-4 backdrop-blur md:hidden">
        <Button variant="ghost" size="icon-sm" aria-label="Open menu" onClick={() => setDrawer(true)}>
          <Menu />
        </Button>
        <Link to="/" className="flex items-center gap-2 text-base font-semibold tracking-tight">
          <Mark className="size-6 shrink-0" />
          Minne
        </Link>
        <div className="flex-1" />
        <Button variant="ghost" size="icon-sm" aria-label="Search" onClick={() => setPalette(true)}>
          <Search />
        </Button>
      </header>

      {/* Mobile drawer */}
      <DialogPrimitive.Root open={drawer} onOpenChange={setDrawer}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 md:hidden" />
          <DialogPrimitive.Content className="fixed bottom-0 left-0 top-[env(safe-area-inset-top)] z-50 w-[min(18rem,85vw)] border-r border-sidebar-border bg-sidebar pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] text-sidebar-foreground shadow-xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left md:hidden">
            <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Spaces and pages</DialogPrimitive.Description>
            <DialogPrimitive.Close className="absolute right-2 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-foreground/5 hover:text-foreground" aria-label="Close menu">
              <X className="size-4" />
            </DialogPrimitive.Close>
            <Sidebar
              onNavigate={() => setDrawer(false)}
              onOpenPalette={() => {
                setDrawer(false);
                setPalette(true);
              }}
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {/* @container/main: page rails size themselves by main's width, so a collapsed sidebar counts. */}
      <main className={cn("@container/main min-w-0", !collapsed && "md:pl-64")}>
        {/* Out of the flow, so showing or hiding the sidebar never moves the page down. */}
        {collapsed && (
          <div className="fixed left-[calc(0.75rem+env(safe-area-inset-left))] top-[calc(0.75rem+env(safe-area-inset-top))] z-30 hidden md:flex">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Show sidebar" onClick={() => setCollapsed(false)}>
                  <PanelLeftOpen />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Show sidebar ({isMac() ? "⌘" : "Ctrl"}B)</TooltipContent>
            </Tooltip>
          </div>
        )}
        {/* When main is too narrow for the button to sit left of the content, pad past it. */}
        <div className={cn("mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 md:py-8", collapsed && "md:@max-[70rem]/main:pl-14")}>
          <Outlet />
        </div>
      </main>

      <CommandPalette open={palette} onOpenChange={setPalette} />
    </div>
  );
}
